// Simple mode (§11.19, M18 layout): one step at a time — 1 Open, 2 Clean up, 3 Filters, 4 Background,
// 5 Save — with "Step n of 5", Back / Next and a step list to jump; Undo, hold-to-compare and a green Save
// pinned at the bottom. Everything edits the same Settings as Pro mode, so switching never loses work.
import { useEffect, useRef, useState, type ComponentType } from 'react';
import {
  Bike,
  Check,
  Contrast,
  Crop,
  Download,
  Eraser,
  Eye,
  FileImage,
  FolderOpen,
  Image as ImageIcon,
  ImagePlus,
  Instagram,
  Lightbulb,
  Megaphone,
  MessageCircle,
  Monitor,
  Palette,
  Ruler,
  Scissors,
  Sparkle,
  SunDim,
  Undo2,
  Utensils,
  UtensilsCrossed,
  Waves,
  type LucideProps,
} from 'lucide-react';
import { useT, type MessageKey } from '../../i18n';
import { useLibrary } from '../../state/library';
import { useEditor } from '../../state/editor';
import { useUi } from '../../state/ui';
import { useSimple } from '../../state/simple';
import { pickFilesFallback } from '../../io/fs';
import { openPhotos } from '../../io/open';
import { openFolder } from '../../state/persist';
import { FallbackFolder } from '../../io/fs';
import {
  CLEANERS,
  CROP_SHAPES,
  cleanerOn,
  cropFor,
  startOver,
  toggleCleaner,
} from '../../features/simpleCleaners';
import { simpleSave, type SaveTarget } from '../../features/simpleActions';
import { useMatte } from '../../features/background/matte';
import { FilterGrid } from './FilterGrid';
import { MakeBigger } from './MakeBigger';
import { makeAdFromPhotos } from '../../design/ad/actions';
import { AiPhotoButtons } from '../dialogs/AiDialogs';
import { useWorkspace } from '../../state/workspace';
import { errorKey } from '../../i18n/errors';
import type { Rgb } from '../../model/types';
import s from './Simple.module.css';

type Icon = ComponentType<LucideProps>;
const CLEANER_ICON: Record<string, Icon> = {
  fixLighting: Lightbulb,
  fixColors: Palette,
  brighten: SunDim,
  tameSky: Contrast,
  cleanPlate: Utensils,
  sharpen: Sparkle,
  reduceGrain: Waves,
  straighten: Ruler,
  removeSpots: Eraser,
  cropFor: Crop,
};
const SAVES: { id: SaveTarget; label: MessageKey; icon: Icon }[] = [
  { id: 'instagram', label: 'saveInstagram', icon: Instagram },
  { id: 'whatsapp', label: 'saveWhatsapp', icon: MessageCircle },
  { id: 'menu', label: 'saveMenu', icon: Monitor },
  { id: 'delivery', label: 'saveDelivery', icon: Bike },
  { id: 'png', label: 'savePng', icon: ImageIcon },
  { id: 'original', label: 'saveOriginal', icon: FileImage },
];
const STEPS: { n: 1 | 2 | 3 | 4 | 5; title: MessageKey }[] = [
  { n: 1, title: 'stepOpen' },
  { n: 2, title: 'stepClean' },
  { n: 3, title: 'stepFilter' },
  { n: 4, title: 'stepBackground' },
  { n: 5, title: 'stepSave' },
];
const hex = (c: Rgb) =>
  '#' +
  c
    .map((v) =>
      Math.round(Math.min(1, Math.max(0, v)) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('');
const ic = (I: Icon) => <I className="icon" aria-hidden="true" />;

export async function openPhotoFiles() {
  const files = await pickFilesFallback();
  if (files.length) await openFolder(new FallbackFolder(files));
}

export function SimplePanel() {
  const t = useT();
  const id = useLibrary((st) => st.activeId);
  const photoCount = useLibrary((st) => st.photos.length);
  const settings = useEditor((st) => (id ? st.settings[id] : undefined)) ?? useEditor.getState().get('');
  const undoCount = useEditor((st) => (id ? (st.history[id]?.past.length ?? 0) : 0));
  const tool = useUi((st) => st.tool);
  const message = useSimple((st) => st.message);
  const step = useSimple((st) => st.step);
  useSimple((st) => st.cleaners); // re-render when a cleaner record changes
  const bgState = useMatte((m) => (id ? m.status[id]?.state : undefined));
  const [cropOpen, setCropOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const has = !!id;
  const go = (n: number) =>
    useSimple.getState().set({ step: Math.min(5, Math.max(1, n)) as 1 | 2 | 3 | 4 | 5 });

  // Opening the first photo moves on to Clean up.
  const hadPhoto = useRef(photoCount > 0);
  useEffect(() => {
    if (photoCount > 0 && !hadPhoto.current && useSimple.getState().step === 1) go(2);
    hadPhoto.current = photoCount > 0;
  }, [photoCount]);

  const tap = async (cid: (typeof CLEANERS)[number]['id']) => {
    if (cid === 'cropFor') {
      setCropOpen(!cropOpen);
      return;
    }
    setBusy(cid);
    useSimple.getState().set({ message: null });
    try {
      await toggleCleaner(cid);
    } finally {
      setBusy(null);
    }
  };
  const save = async (target: SaveTarget) => {
    setBusy(`save-${target}`);
    setSaveMsg(null);
    try {
      setSaveMsg(t('saved', { name: await simpleSave(target) }));
    } catch (e) {
      console.warn('save failed', e);
      setSaveMsg(t('saveFailed', { reason: t(errorKey(e)) }));
    } finally {
      setBusy(null);
    }
  };
  const bg = settings.background;
  const cur = STEPS[step - 1]!;

  return (
    <div data-testid="simple-panel" className={s.panel}>
      <nav className={s.steps} aria-label={t('stepOf', { n: step, total: 5 })}>
        {STEPS.map((st) => (
          <button
            key={st.n}
            className={s.stepPill}
            data-testid={`simple-step-${st.n}`}
            aria-current={st.n === step ? 'step' : undefined}
            onClick={() => go(st.n)}
          >
            <span className={s.num}>{st.n}</span>
            {t(st.title)}
          </button>
        ))}
      </nav>
      <div className={s.head}>
        <div className={s.of} data-testid="simple-step-of">
          {t('stepOf', { n: step, total: 5 })}
        </div>
        <h2>{t(cur.title)}</h2>
      </div>

      <div className={s.body}>
        {step === 1 && (
          <>
            <button className={s.primaryBtn} data-testid="simple-open" onClick={() => void openPhotoFiles()}>
              {ic(ImagePlus)} {t('simpleOpen')}
            </button>
            <button className={s.linkBtn} onClick={() => void openPhotos()}>
              {ic(FolderOpen)} {t('simpleOpenFolder')}
            </button>
            {photoCount > 1 && <div className={s.note}>{t('photoCount', { n: photoCount })}</div>}
          </>
        )}

        {step === 2 && (
          <>
            <div className={s.grid}>
              {CLEANERS.filter((c) => c.id !== 'removeBackground').map((c) => {
                const on = has && cleanerOn(id!, c.id, settings);
                const I = CLEANER_ICON[c.id] ?? Sparkle;
                return (
                  <button
                    key={c.id}
                    className={s.btn}
                    data-testid={`cleaner-${c.id}`}
                    aria-pressed={on}
                    disabled={!has || (!!busy && busy !== c.id)}
                    onClick={() => void tap(c.id)}
                  >
                    <span className={s.lead}>{on ? ic(Check) : ic(I)}</span>
                    <span>{t(c.label)}</span>
                    {busy === c.id ? ' …' : ''}
                  </button>
                );
              })}
            </div>
            {cropOpen && (
              <div className={s.grid} style={{ marginTop: 8 }} data-testid="crop-shapes">
                {CROP_SHAPES.map((c) => (
                  <button
                    key={c.aspect}
                    className={s.btn}
                    onClick={() => cropFor(c.aspect)}
                    data-testid={`crop-${c.aspect}`}
                  >
                    {t(c.label)}
                  </button>
                ))}
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <MakeBigger />
            </div>
            <AiPhotoButtons />
            {tool === 'spot' && <div className={s.note}>{t('clSpotsHint')}</div>}
            {tool === 'cropPan' && <div className={s.note}>{t('clCropHint')}</div>}
            {message && (
              <div role="status" data-testid="simple-message" className={s.note}>
                {t(message as MessageKey)}
              </div>
            )}
            <button className={s.linkBtn} data-testid="simple-start-over" disabled={!has} onClick={startOver}>
              {t('startOver')}
            </button>
          </>
        )}

        {step === 3 && <FilterGrid />}

        {step === 4 && (
          <>
            <button
              className={s.wide}
              data-testid="simple-bg"
              aria-pressed={bg.mode !== 'off'}
              disabled={!has || busy === 'bg'}
              onClick={async () => {
                setBusy('bg');
                try {
                  await toggleCleaner('removeBackground');
                } catch {
                  /* the status line below says why */
                } finally {
                  setBusy(null);
                }
              }}
            >
              {bg.mode !== 'off' ? ic(Check) : ic(Scissors)} {t('clRemoveBackground')}
              {bgState === 'loading' || bgState === 'running' ? ' …' : ''}
            </button>
            {bgState === 'error' && <div className={s.err}>{t('bgFailedSimple')}</div>}
            {bg.mode !== 'off' && id && (
              <div role="radiogroup" style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                <button
                  role="radio"
                  className={s.btn}
                  aria-checked={bg.fill.kind === 'transparent'}
                  aria-pressed={bg.fill.kind === 'transparent'}
                  data-testid="simple-bg-transparent"
                  onClick={() =>
                    useEditor
                      .getState()
                      .update(
                        id,
                        (st) => void (st.background.fill = { ...st.background.fill, kind: 'transparent' }),
                      )
                  }
                  style={{ flex: 1 }}
                >
                  {t('bgTransparent')}
                </button>
                <button
                  role="radio"
                  className={s.btn}
                  aria-checked={bg.fill.kind === 'color' && bg.fill.color.every((v) => v >= 0.999)}
                  aria-pressed={bg.fill.kind === 'color' && bg.fill.color.every((v) => v >= 0.999)}
                  data-testid="simple-bg-white"
                  onClick={() =>
                    useEditor
                      .getState()
                      .update(id, (st) => void (st.background.fill = { kind: 'color', color: [1, 1, 1] }))
                  }
                  style={{ flex: 1 }}
                >
                  {t('bgWhite')}
                </button>
                <input
                  type="color"
                  aria-label={t('bgColor')}
                  value={hex(bg.fill.color)}
                  onChange={(e) =>
                    useEditor.getState().update(id, (st) => {
                      const h = e.target.value;
                      st.background.fill = {
                        kind: 'color',
                        color: [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as Rgb,
                      };
                    })
                  }
                  style={{ width: 44, height: 44, padding: 2 }}
                />
              </div>
            )}
          </>
        )}

        {step === 5 && (
          <>
            <div className={s.grid}>
              {SAVES.map((sv) => (
                <button
                  key={sv.id}
                  className={s.btn}
                  data-testid={`save-${sv.id}`}
                  disabled={!has || !!busy}
                  onClick={() => void save(sv.id)}
                >
                  <span className={s.lead}>{ic(sv.icon)}</span>
                  <span>{t(sv.label)}</span>
                  {busy === `save-${sv.id}` ? ' …' : ''}
                </button>
              ))}
            </div>
            {saveMsg && (
              <div role="status" data-testid="simple-save-status" className={s.note}>
                {saveMsg}
              </div>
            )}
            <button
              className={s.wide}
              data-testid="make-ad"
              disabled={!!busy}
              title={t('makeAdHint')}
              onClick={makeAdFromPhotos}
            >
              {ic(Megaphone)} {t('makeAd')}
            </button>
            <button
              className={s.wide}
              data-testid="plating-guide"
              onClick={() => useWorkspace.getState().setWs('plating')}
            >
              {ic(UtensilsCrossed)} {t('platingGuide')}
            </button>
          </>
        )}
      </div>

      <div className={s.nav}>
        <button data-testid="simple-back" disabled={step === 1} onClick={() => go(step - 1)}>
          {t('stepBack')}
        </button>
        <button data-testid="simple-next" disabled={step === 5} onClick={() => go(step + 1)}>
          {t('stepNext')}
        </button>
      </div>
      <div className={s.foot}>
        <button
          className={s.quiet}
          data-testid="simple-undo"
          disabled={!undoCount}
          onClick={() => id && useEditor.getState().undo(id)}
        >
          {ic(Undo2)} {t('undoBig')}
        </button>
        <button
          className={s.quiet}
          data-testid="simple-hold-original"
          disabled={!has}
          title={t('holdOriginal')}
          aria-label={t('holdOriginal')}
          onPointerDown={() => useUi.getState().set({ before: true })}
          onPointerUp={() => useUi.getState().set({ before: false })}
          onPointerLeave={() => useUi.getState().set({ before: false })}
        >
          {ic(Eye)}
        </button>
        <button className={s.save} data-testid="simple-save" disabled={!has} onClick={() => go(5)}>
          {ic(Download)} {t('saveBig')}
        </button>
      </div>
    </div>
  );
}
